/**
 * The bank report templates, with no browser or Apollo dependencies, so server code (the MCP
 * route in pages/api/mcp.js) renders and validates exactly the form the app shows.
 * bank-service re-exports these; import from here in server code.
 */
import { fake_valuation_form_response } from "../data/ireland_valuation_form";
import { createPfiForm } from "../data/pfi_valuation_form";
import { boi_valuation_form_response } from "../data/boi_valuation_form";
import { getBankConfig, resolveFormType } from "../data/bankConfig";
import { createBankForm } from "../data/bankFormFactory";

// A template is either a ready object or a factory of bankConfig, so lender
// names live in bankConfig rather than in the template text. A bank that
// offers several report forms (bankConfig.formTypes) maps each form id to its
// template; the valuation's formType picks one, its first formType is the
// default.
const BANK_FORM_TEMPLATES = {
  "Probate Finance Ireland Ltd": {
    "probate-bridging": createPfiForm,
    residential: createBankForm,
  },
  "Bank of Ireland": boi_valuation_form_response,
};

const DEFAULT_FORM_TEMPLATE = fake_valuation_form_response;

// Cache generated forms so we don't re-clone on every access
const generatedFormCache = {};

const build = (cacheKey, template, config) => {
  if (typeof template !== "function") return template;
  if (!generatedFormCache[cacheKey]) generatedFormCache[cacheKey] = template(config);
  return generatedFormCache[cacheKey];
};

export function getFormTemplate(bankName, formType) {
  const config = getBankConfig(bankName);
  let template = BANK_FORM_TEMPLATES[bankName];

  // Several forms: pick by the (resolved) form type
  if (template && typeof template === "object" && !template.data) {
    const type = resolveFormType(bankName, formType);
    return build(`${bankName}:${type}`, template[type], config);
  }

  // Hardcoded templates take priority (backward compatible)
  if (template) return build(bankName, template, config);

  // Generate from factory for known banks
  if (config.shortName !== "?") return build(bankName, createBankForm, config);

  return DEFAULT_FORM_TEMPLATE;
}

export const transformTemplatePanels = rawPanels => {
  const extractMeta = item => {
    if (item.meta) {
      const metafield = JSON.parse(item.meta);
      delete item.meta;
      return metafield;
    }
    return;
  };
  const panels = JSON.parse(JSON.stringify(rawPanels));
  return panels.map(form => ({
    ...form,
    subPanels: form.subPanels?.map(subPanel => {
      const metaFields = extractMeta(subPanel);
      return {
        ...subPanel,
        ...metaFields
      };
    }),
    fieldsGroups: form.fieldsGroups?.map(group => {
      const metaFields = extractMeta(group);
      return {
        ...group,
        ...metaFields
      };
    }),
    fields: form.fields.map(field => {
      const metaFields = extractMeta(field);
      Object.keys(field).map(key => {
        if (field[key] === null) delete field[key];
      });
      return {
        ...field,
        ...metaFields
      };
    })
  }));
};
